import React, { useState, useEffect } from 'react';
import { Plus, LogOut, User } from 'lucide-react';

const HeaderSection = ({ userEmail, onAddNewRow, onLogout }) => {
  const [showUserMenu, setShowUserMenu] = useState(false);

  // Close user menu when clicking outside or pressing Escape
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showUserMenu && !event.target.closest('.user-menu-container')) {
        setShowUserMenu(false);
      }
    };

    const handleKeyDown = (event) => {
      if (event.key === 'Escape' && showUserMenu) {
        setShowUserMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [showUserMenu]);

  return (
    <header className="bg-white/80 backdrop-blur-sm border-b border-gray-200/60 sticky top-0 z-40" role="banner">
      <div className="max-w-full mx-auto px-8 py-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
              Naming Tool
            </h1>
            <p className="text-sm text-gray-600 mt-1 font-medium">Manage and track all project file naming with ease</p>
          </div>
          
          <div className="flex items-center space-x-4" role="toolbar" aria-label="Main actions">
            {/* New File Button with Template Options */}
            <div className="relative">
              <button
                onClick={onAddNewRow}
                className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-2xl hover:from-blue-700 hover:to-blue-800 focus:from-blue-700 focus:to-blue-800 transition-all duration-200 shadow-lg hover:shadow-xl focus:shadow-xl text-sm font-semibold focus:outline-none focus:ring-4 focus:ring-blue-200"
                aria-label="Create a new file record"
              >
                <Plus className="w-5 h-5 mr-2" aria-hidden="true" />
                New File
              </button>
              
              {/* Template functionality disabled in this build */}
            </div>

            {/* User Avatar with Dropdown */}
            <div className="relative user-menu-container">
              <button
                onClick={() => setShowUserMenu(!showUserMenu)}
                className="w-10 h-10 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full flex items-center justify-center hover:from-blue-600 hover:to-purple-600 focus:from-blue-600 focus:to-purple-600 transition-all duration-200 shadow-lg hover:shadow-xl focus:shadow-xl focus:outline-none focus:ring-4 focus:ring-blue-200"
                aria-label={`User menu for ${userEmail}`}
                aria-expanded={showUserMenu}
                aria-haspopup="menu"
              >
                <User className="w-5 h-5 text-white" aria-hidden="true" />
              </button>

              {/* Dropdown Menu */}
              {showUserMenu && (
                <div 
                  className="absolute right-0 mt-2 w-56 bg-white/95 backdrop-blur-sm border border-gray-200/60 rounded-2xl shadow-xl z-50 overflow-hidden"
                  role="menu"
                  aria-label="User menu options"
                >
                  <div className="px-4 py-3 border-b border-gray-200/60 bg-gradient-to-r from-blue-50 to-purple-50" role="presentation">
                    <div className="text-xs font-semibold text-gray-800 truncate">{userEmail}</div>
                    <div className="text-[10px] text-gray-500">Logged in</div>
                  </div>
                  <div className="py-2" role="presentation">
                    <button
                      onClick={onLogout}
                      className="w-full px-4 py-3 text-left text-xs font-medium text-gray-700 hover:bg-red-50 hover:text-red-600 focus:bg-red-50 focus:text-red-600 focus:outline-none transition-all duration-200 flex items-center space-x-3"
                      role="menuitem"
                      aria-label="Sign out of your account"
                    >
                      <LogOut className="w-4 h-4" aria-hidden="true" />
                      <span>Logout</span>
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </header>
  );
};

export default HeaderSection;